import React, { useState } from "react";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import {
  AdminTableBody,
  AdminMainText,
  AdminPanelTitle,
  AdminStatusBadge,
  AdminSupportingText,
  AdminLinkAction,
} from "../../sections/AdminDashboard/shared/adminUx";
import LoginSecurityPopup from "./LoginSecurityPopup";
import useAuth from "../../Hooks/useAuth";

const LoginSecurityWidget = ({ values = {} }) => {
  const { auth } = useAuth();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(0);
  const [localValues, setLocalValues] = useState(values);

  // Mapping between widget field indices and popup step indices
  const fieldToPopupMapping = {
    0: null, // Membership Status (widget index 0) -> no popup
    1: null, // Email address (widget index 1) -> no popup (not changeable)
    2: 0, // Password (widget index 2) -> popup step 0
    3: 4, // Mobile number (widget index 3) -> popup step 4
    4: null, // Account Created (widget index 4) -> no popup
    5: null  // Current session (widget index 5) -> no popup
  };

  // Define fields with verification status and display type
  const fields = [
    { name: "Membership Status", key: "membershipStatus", verified: false, displayType: "value", buttonText: null },
    { name: "Email address", key: "email", verified: true, displayType: "tag", buttonText: null },
    { name: "Password", key: "password", verified: true, displayType: "value", buttonText: "Change" },
    { name: "Mobile number", key: "mobile", verified: false, displayType: "tag", buttonText: "Verify" },
    { name: "Account Created", key: "accountCreated", verified: true, displayType: "value", buttonText: null },
    { name: "Current session", key: "currentSession", verified: true, displayType: "value", buttonText: null }
    // { name: "Account Verification", key: "accountVerification", verified: true, displayType: "tag", buttonText: null }
  ];

  // Get membership status from auth
  const membershipStatus = auth?.userPlan?.plan?.name 
    ? `${auth.userPlan.plan.name.charAt(0).toUpperCase() + auth.userPlan.plan.name.slice(1)}`
    : "";

  // Sample data for demonstrationmp
  const sampleData = {
    email: "john@acme.com.au",
    password: "••••••••",
    mobile: "0400 123 456",
    mobileVerified: false, // Track mobile verification status separately
    currentSession: {
      device: "Windows 10",
      location: "Sydney, Australia",
      ipAddress: "192.168.1.100",
      lastActivity: "2 minutes ago",
      isCurrent: true
    },
    // Account Details from PublicContactWidget
    accountCreated: "March 20, 2020",
    lastLogin: "August 22, 2024",
    membershipStatus: membershipStatus,
    accountVerification: "Verified"
  };


  // Merge sample data with actual values
  const displayValues = {
    email: (localValues && localValues.email) || sampleData.email || "",
    password: (localValues && localValues.password) || sampleData.password || "",
    mobile: (localValues && localValues.mobile) || sampleData.mobile || "",
    mobileVerified: (localValues && localValues.mobileVerified !== undefined) ? localValues.mobileVerified : sampleData.mobileVerified,
    currentSession: (localValues && localValues.currentSession) || sampleData.currentSession || "",
    // Account Details from PublicContactWidget
    accountCreated: (localValues && localValues.accountCreated) || sampleData.accountCreated || "",
    lastLogin: (localValues && localValues.lastLogin) || sampleData.lastLogin || "",
    membershipStatus: (localValues && localValues.membershipStatus) || sampleData.membershipStatus || "",
    accountVerification: (localValues && localValues.accountVerification) || sampleData.accountVerification || ""
  };


  return (
    <>
      <DashboardMainContainer className="masonry-item w-[550px] h-auto account-widget">
        <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
          <AdminPanelTitle className="text-offblack">Login & Security</AdminPanelTitle>
        </div>
        
        <div className="px-4 py-4">
          {/* Fields List */}
          <div className="space-y-0">
            {fields.map((field, index) => {
              const hasValue = displayValues[field.key] && 
                (typeof displayValues[field.key] === 'object' ? 
                  Object.keys(displayValues[field.key]).length > 0 : 
                  displayValues[field.key].toString().trim() !== "");
              const isTag = field.displayType === "tag";
              
              // Determine if we need a border after this field
              const needsBorder = index === 0 || (index >= 2 && index < fields.length - 1); // Membership status (index 0) and from password (index 2) to second last field
              
              return (
                <div key={field.key} className={`flex items-center justify-between ${needsBorder ? 'border-b border-mediumgray' : ''} ${field.key === 'email' ? 'pt-3 pb-0' : field.key === 'password' ? 'pt-2 pb-3' : 'py-3'}`}>
                  <div className={`flex ${field.key === 'currentSession' ? 'items-start' : 'items-center'} justify-center w-full`}>
                    <AdminTableBody emphasis className="w-56 text-offblack shrink-0">
                      {field.name}
                    </AdminTableBody>
                    <div className="flex items-start justify-between flex-1 min-w-0">
                      <AdminMainText className="text-offblack truncate">
                        {isTag ? (
                          <div className="flex items-center gap-2">
                            <span>
                              {hasValue ? displayValues[field.key] : 'Not set'}
                            </span>
                            {field.key === 'mobile' ? (
                              <AdminStatusBadge
                                status={displayValues.mobileVerified ? 'Active' : 'Escalated'}
                                label={displayValues.mobileVerified ? 'Verified' : 'Unverified'}
                              />
                            ) : (
                              <AdminStatusBadge status="Active" label="Verified" />
                            )}
                          </div>
                        ) : field.key === 'currentSession' && hasValue ? (
                          <div className="space-y-0.5">
                            <AdminSupportingText className="block py-0.5 text-offblack">
                              Device: {displayValues[field.key].device}
                            </AdminSupportingText>
                            <AdminSupportingText className="block py-0.5 text-offblack">
                              Location: {displayValues[field.key].location}
                            </AdminSupportingText>
                            <AdminSupportingText className="block py-0.5 text-offblack">
                              IP Address: {displayValues[field.key].ipAddress}
                            </AdminSupportingText>
                            <AdminSupportingText className="block py-0.5 text-offblack">
                              Last Activity: {displayValues[field.key].lastActivity}
                            </AdminSupportingText>
                          </div>
                        ) : (
                          <span>
                            {hasValue ? displayValues[field.key] : 'Not set'}
                          </span>
                        )}
                      </AdminMainText>
                      {field.buttonText && (
                        <AdminLinkAction
                          type="button"
                          className="ml-2 shrink-0"
                          onClick={() => {
                            // Use mapping to get correct popup step index
                            const popupStepIndex = fieldToPopupMapping[index];
                            if (popupStepIndex !== undefined) {
                              setSelectedFieldIndex(popupStepIndex);
                              setIsPopupOpen(true);
                            }
                          }}
                        >
                          {field.buttonText}
                        </AdminLinkAction>
                      )}
                      {field.key === 'currentSession' && hasValue && (
                        <button className="group relative bg-freshgreen hover:bg-alertred text-white px-3 py-1 rounded-full widgets-status-badge-legend transition-colors duration-150 cursor-pointer ml-2">
                          <span className="invisible block">Active</span>
                          <span className="absolute inset-0 flex items-center justify-center group-hover:hidden">Active</span>
                          <span className="absolute inset-0 hidden group-hover:flex items-center justify-center">Revoke</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </DashboardMainContainer>

      {/* Login Security Popup */}
      <LoginSecurityPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        setFieldValue={(field, value) => {
          // Handle field value updates
          console.log(`Update ${field} to ${value}`);
          
          // Update local state for mobile verification
          if (field === 'mobile') {
            setLocalValues(prev => ({
              ...prev,
              [field]: value,
              mobileVerified: true // Set verified to true after successful verification
            }));
          } else {
            setLocalValues(prev => ({
              ...prev,
              [field]: value
            }));
          }
        }}
        values={localValues}
        errors={{}}
        initialStep={selectedFieldIndex}
      />

    </>
  );
};

export default LoginSecurityWidget;
